import { defineStore } from 'pinia'
import httpClient from '../http-client.js';
import { dashboardApiEndpointConsts, globalValueConsts } from '../constants.js'

const ZIP_CODE_SEARCH_MODE = Object.freeze({
    INTERNAL: 'internal',
    EXTERNAL: 'external'
});
  
export const useUserStore = defineStore('user', {
    state: () => ({
        activeAgencyNumber: null,
        agencyList: [],
        defaultAgencyNumber: null,
        removedUserXYZAccountsCount: 0,
        searchedUsersAgencyAccountText: null,
        userAccountsCount: null,
        userRole: null,
        SearchedUsersAgentNo: null,
        searchedUsersAgentNameOrEmailText: null,
        isSSOLogin: false, // Add isSSOLogin flag
        parentAccountNumber: 0, // Add parentAccountNumber for sso
        ssoFlowConfig: null,
        userDetails: null // Add userDetails to store
    }),
    persist: true,
    getters: {
        //Returns whether the active agency is dedicated.
        isDedicatedActiveAgency() {
            let activeAgency = this.agencyList?.find((item) => item.agencyNumber == this.activeAgencyNumber)

            if(activeAgency != null) {
                return activeAgency.priority === "D";
            } else {
                return false;
            }
        },
        activeAgencyParent() {
            let activeAgency = this.agencyList?.find((item) => item.agencyNumber == this.activeAgencyNumber)

            if(activeAgency != null) {
                return activeAgency.parentAccountNumber;
            } else {
                return 0;
            }
        },
        // Safely retrieves onboarding agency number, handling both naming conventions from backend
        ssoOnboardingAgencyNumber() {
            return this.ssoFlowConfig?.onboardingAgencyNumber
                || this.ssoFlowConfig?.OnboardingAgencyNumber;
        },
        // Safely retrieves useOnboardingAgency flag from ssoFlowConfig
        useOnboardingAgencyConfig() {
            return this.ssoFlowConfig?.useOnboardingAgency;
        },
        // Get the SSO client id from configuration (e.g., 'allstate', 'statefarm')
        ssoClientId() {
            return this.ssoFlowConfig?.id?.toLowerCase() || null;
        },
        // Config-driven toggle for showing agency name in the header during SSO claim flows.
        // For now, use a simple allow-list of SSO client ids.
        shouldShowAgencyNameInFileClaimHeader() {
            if (!this.isSSOLogin) {
                return true;
            }

            const clientsThatShowAgencyName = ["allstate"];
            return clientsThatShowAgencyName.includes(this.ssoClientId);
        },
        // Determines whether File a claim should redirect to ISS for the active SSO client.
        // Supports multiple property names for backward compatibility with config payloads.
        shouldRedirectFileClaimToIss() {
            if (!this.isSSOLogin) {
                return false;
            }

            const explicitIssRedirectFlag = this.ssoFlowConfig?.useOnlyIssForFileClaim;

            // Config-driven behavior only: if unset, do not force ISS redirect.
            return explicitIssRedirectFlag ?? false;
        },
        zipCodeSearchMode() {
            if (!this.isSSOLogin) {
                return ZIP_CODE_SEARCH_MODE.EXTERNAL;
            }

            const configuredZipSearchMode = this.ssoFlowConfig?.zipCodeSearchMode;
            if (typeof configuredZipSearchMode === 'string') {
                const zipSearchMode = configuredZipSearchMode.toLowerCase();
                if (zipSearchMode === ZIP_CODE_SEARCH_MODE.INTERNAL || zipSearchMode === ZIP_CODE_SEARCH_MODE.EXTERNAL) {
                    return zipSearchMode;
                }
            }

            return ZIP_CODE_SEARCH_MODE.EXTERNAL;
        }
    },
    actions: {
        loadSsoFlowConfigByParentAccountAsync(args) {
            const parentAccountNumber = args?.parentAccountNumber;
            const normalizedParentAccountNumber = parentAccountNumber == null
                ? ""
                : String(parentAccountNumber).trim();

            if (normalizedParentAccountNumber === "") {
                this.ssoFlowConfig = null;
                return Promise.resolve(null);
            }

            const ssoClientConfigEndpoint = dashboardApiEndpointConsts.GET_SSO_CLIENT_CONFIG + "?" + "key=" + encodeURIComponent(normalizedParentAccountNumber);

            return httpClient.callHttpClient({
                method: 'GET',
                endpoint: ssoClientConfigEndpoint,
                bearerToken: args?.bearerToken,
                payload: {},
            }).then((response) => {
                const responseData = response?.data;
                let resolvedConfig = responseData;

                // Some environments return a key/value payload where value is serialized JSON.
                if (responseData && typeof responseData === "object" && responseData.value != null) {
                    const rawValue = responseData.value;
                    if (typeof rawValue === "string") {
                        try {
                            resolvedConfig = JSON.parse(rawValue);
                        } catch {
                            resolvedConfig = null;
                        }
                    } else {
                        resolvedConfig = rawValue;
                    }
                }

                this.ssoFlowConfig = resolvedConfig || null;
                return this.ssoFlowConfig;
            }).catch(() => {
                this.ssoFlowConfig = null;
                return null;
            });
        },
        addAgencyToUserAsync(args) {
            return httpClient.callHttpClient({
                method: 'POST',
                endpoint: dashboardApiEndpointConsts.ADD_AGENCY_TO_USER,
                payload: {
                    "AgencyNumber": args.agency.AgencyNumber,
                    "UserId": args.agency.UserId,
                    "CarrierList": args.agency.CarrierList,
                    "IsDefaultAgency":args.agency.IsDefaultAgency,
                }
            });
        },
        addUserResetPwdLinkAsync(args) {
            return httpClient.callHttpClient({
                method: 'POST',
                endpoint: dashboardApiEndpointConsts.ADD_USER_RESET_PWD_LINK,
                payload: args.resetPasswordDetails,
            });
        },
        approveAdminRequestsAsync(args) {
            return httpClient.callHttpClient({
                method: 'POST',
                endpoint: dashboardApiEndpointConsts.UPDATE_APPROVED_REQUESTS,
                payload: { "UserId": args.userId },
            }).then(function (response) {
                return response.data;
            });
        },
        changeUserPasswordAsync(args) {
            return httpClient.callHttpClient({
                method: 'POST',
                endpoint: dashboardApiEndpointConsts.CHANGE_USER_PASSWORD,
                payload: args.changePasswordModel,
            }).then(function (response) {
                return response;
            });
        },
        checkUserStatusAsync(args) {
            return httpClient.callHttpClient({
                method: 'GET',
                endpoint: dashboardApiEndpointConsts.GET_USER_STATUS + args.userName,
                payload: {},
            }).then(function (response) {
                return response.data;
            });
        },
        clearAgencyNotificationFlagAsync(args) {
            return httpClient.callHttpClient({
                method: 'POST',
                endpoint: dashboardApiEndpointConsts.CLEAR_NOTIFICATION_FLAG,
                payload:{ "UserId" : args.userId },
            }).then(function (response) {
                this.agencyList.forEach(a => a.showAgencyChangeNotification = false);                
            });
        },
        createActiveUserAsync(args) {
            return httpClient.callHttpClient({
                method: 'POST',
                endpoint: dashboardApiEndpointConsts.GET_CREATE_ACTIVE_USER,
                payload: args.agentInformation,
            }).then(function (response) {
                return response;
            });
        },
        createInactiveUserAsync(args) {
            return httpClient.callHttpClient({
                method: 'POST',
                endpoint: dashboardApiEndpointConsts.GET_CREATE_USER,
                payload: args.createUserModel,
            }).then(function (response) {
                return response;
            });
        },
        createUserPasswordAsync(args) {
            return httpClient.callHttpClient({
                method: 'POST',
                endpoint: dashboardApiEndpointConsts.CREATE_USER_PASSWORD,
                payload: args.createNewPasswordDetails,
            }).then(function (response) {
                return response;
            });
        },
        deactivateUserAsync(args) {
            return httpClient.callHttpClient({
                method: 'GET',
                endpoint: dashboardApiEndpointConsts.DEACTIVATE_USER + "/" + args.userName,
                payload: {},
            }).then(function (response) {
                return response.data;
            });
        },
        denyAdminRequestsAsync(args) {
            return httpClient.callHttpClient({
                method: 'POST',
                endpoint: dashboardApiEndpointConsts.UPDATE_DENIED_REQUESTS,
                payload: { "UserId": args.userId },
            }).then(function (response) {
                return response.data;
            });
        },
        execRemoveUserFromAgencyAsync(args) {
            return httpClient.callHttpClient({
                method: 'POST',
                endpoint: dashboardApiEndpointConsts.REMOVE_USER_FROM_AGENCY,
                payload: { "userId": args.userId, "AGNT_NUM":  args.agentNumber},
            }).then(function (response) {
                return response.data;
            });
        },
        execSendPasswordRecoveryLinkAsync(args) {
            return httpClient.callHttpClient({
                method: 'POST',
                endpoint: dashboardApiEndpointConsts.POST_FORGOTPASSWORD,
                payload: {
                    username: args.userName,
                    factorType: args.factorType
                },
            }).then(function (response) {
                return response;
            });
        },
        execSendRequestAdminAccessEmailAsync(args) {
            return httpClient.callHttpClient({
                method: 'POST',
                endpoint: dashboardApiEndpointConsts.SEND_REQUEST_ADMIN_ACCESS_EMAIL,
                payload: args.user,
                ignoreErrors: true
            }).then(function (response) {
                return response.data;
            });
        },
        getAdminRequestsAsync(args) {
            return httpClient.callHttpClient({
                method: 'GET',
                endpoint: dashboardApiEndpointConsts.GET_ADMIN_REQUESTS + "?v="+ Date.now(),
                payload: {},
            }).then(function (response) {
                return response.data;
            });
        },
        getIsRegisteredUserAsync(args) {
            return httpClient.callHttpClient({
                method: 'GET',
                endpoint: dashboardApiEndpointConsts.GET_IS_REGISTERED_USER + "/" + args.courseID + "/" + args.oktaID,
                payload: {},
            }).then(function (response) {
                return response.data;
            });
        },
        getListOktaUsersAsync(args) {
            return httpClient.callHttpClient({
                method: 'POST',
                endpoint: dashboardApiEndpointConsts.GET_OKTA_AGENT_LIST,
                payload: {
                    AgentNumber: args.agentNumber,
                    SearchTerm: args.searchTerm,
                    SortBy: args.sortBy,
                    SortOrder: args.sortOrder,
                    ShouldFilterByRole:args.shouldFilterByRole,
                    RoleToFilterBy: args.roleToFilterBy
                },
            }).then(function (response) {
                return response.data;
            });
        },
        getOktaUsersByAgentNameOrEmailAsync(args)
        {
            return httpClient.callHttpClient({
                method: 'POST',
                endpoint: dashboardApiEndpointConsts.GET_OKTA_AGENTS_LIST_By_NAMEOrEMAIL,
                payload: {
                    SearchTerm: args.searchTerm,
                    SortBy: args.sortBy,
                    SortOrder: args.sortOrder,
                    ShouldFilterByRole: args.shouldFilterByRole,
                    RoleToFilterBy: args.roleToFilterBy
                },
            }).then(function (response) {
                return response.data;
            });
        },
        getSignInDetailsAsync(args) {
            return httpClient.callHttpClient({
                method: 'POST',
                endpoint: dashboardApiEndpointConsts.GET_SIGN_IN_DETAILS,
                payload: {
                    userId: args.userId,
                    AgencyNumber : args.agencyNumber
                },
            }).then(function (response) {
                return response;
            });
        },
        getUserDetailsAsync(args) {
            return httpClient.callHttpClient({
                method: 'POST',
                endpoint: dashboardApiEndpointConsts.GET_USER_DETAILS,
                payload: { 
                    userId: args.userId,
                    isCobranded: args.isCobranded,
                    AgencyNumber: args.agencyNumber
                },
            }).then(function (response) {
                return response;
            });
        },
        getUserProfileAsync(args) {
            return httpClient.callHttpClient({
                method: 'GET',
                endpoint: dashboardApiEndpointConsts.GET_USER_PROFILE + args.userId,
                payload: { 
                    userId: args.userId
                },
            }).then(function (response) {
                return response;
            });
        },
        getUserResetPwdLinkAsync(args) {
            return httpClient.callHttpClient({
                method: 'GET',
                endpoint: dashboardApiEndpointConsts.GET_USER_RESET_PWD_LINK + "?" + "guid=" + args.guid,
                payload: {},
            }).then(function (response) {
                return response;
            });
        },
        setUserAgencies(agencies) {
            var defaultAgency = null;

            if (agencies.length > 0) {
                var defaultAgencies = agencies.filter(agent => agent.isDefaultAgency);
                if (defaultAgencies && defaultAgencies.length > 0) {
                    defaultAgency = defaultAgencies[0].agencyNumber;
                    this.activeAgencyNumber = defaultAgency;
                    this.defaultAgencyNumber = defaultAgency;
                }

                this.agencyList = [];
                agencies.forEach(agency => {
                    this.agencyList.push(agency)
                });
            }

            return defaultAgency;
        },
        updateAgentContactPrefsAsync(args) {
            return httpClient.callHttpClient({
                method: 'POST',
                endpoint: dashboardApiEndpointConsts.UPDATE_AGENT_CONTACT_PREFS,
                payload: args.userData,
            }).then(function (response) {
                return response;
            });
        },
        updateAgentInformationAsync(args) {
            return httpClient.callHttpClient({
                method: 'POST',
                endpoint: dashboardApiEndpointConsts.UPDATE_AGENT_INFORMATION,
                payload: args.agentInformation,
            }).then(function (response) {
                return response;
            });
        },
        assignUserToGroupAsync(args) {
            return httpClient.callHttpClient({
                method: 'POST',
                endpoint: dashboardApiEndpointConsts.ASSIGN_USER_GROUP,
                payload: {
                    "oktaId": args.oktaId,
                    "groupName": args.groupName
                },
                bearerToken: args.bearerToken
            }).then(function (response) {
                return response.data;
            });
        },
        updateOktaUserProfileAsync(args) {
            return httpClient.callHttpClient({
                method: 'POST',
                endpoint: dashboardApiEndpointConsts.UPDATE_OKTAUSER_PROFILE,
                payload: {
                    "oktaId": args.oktaId,
                    "fieldName": args.fieldName,
                    "fieldValue": args.fieldValue
                },
                bearerToken: args.bearerToken
            }).then(function (response) {
                return response.data;
            });
        },
        updateUserAgentCarriersAsync(args) {
            return httpClient.callHttpClient({
                method: 'POST',
                endpoint: dashboardApiEndpointConsts.UPDATE_USER_AGENT_CARRIERS,
                payload: args.agentInformation,
            }).then(function (response) {
                return response;
            });
        },
        updateUserDefaultAgencyAsync(args) {
            return httpClient.callHttpClient({
                method: 'POST',
                endpoint: dashboardApiEndpointConsts.UPDATE_USER_DEFAULT_AGENCY,
                payload: {
                    "UserId": args.userId,
                    "AGNT_NUM":  args.agencyNumber,
                    "ShowAgencyChangeNotification": args.showAgencyChangeNotification
                },
                bearerToken: null //Passing bearer token as user's token has not made it to local storage yet.
            }).then(function (response) {
                return response.data;
            });
        },
        execResetMfaAsync(args){
            return httpClient.callHttpClient({
              method: 'POST',
              endpoint: dashboardApiEndpointConsts.RESETMFA,
              payload: { "UserId": args.userId }
            })
            .then(function (response) {
              return response;
            });
          },
          notifyUserLoggedInAsync(args) {
            return httpClient.callHttpClient({
                method: 'GET',
                endpoint: dashboardApiEndpointConsts.NOTIFY_USER_LOGGED_IN + args.userName,
                payload: {},
            }).then(function (response) {
                return response;
            });
        },
        setUserDetails(details) {
            this.userDetails = details; // Always use this action to update userDetails
        },
        setUserDetailsInStoreSSOAsync(args) {
            var self = this;

            return httpClient.callHttpClient({
                method: 'POST',
                endpoint: dashboardApiEndpointConsts.GET_USER_DETAILS_SSO,
                payload: { 
                    UserId: args.userId,
                    AgencyNumber: args.agencyNumber
                },
            }).then(function (response) {
                var defaultAgency =  response.data.AgencyList.find(a => a.isDefaultAgency);

                self.userDetails = response.data.UserDetails;
                self.activeAgencyNumber = defaultAgency.agencyNumber;
                self.defaultAgencyNumber = defaultAgency.agencyNumber;
                self.agencyList = response.data.AgencyList;
           });
           
        },
        updateOnboardingClaimsAsync(args) {
            return httpClient.callHttpClient({
                method: 'POST',
                endpoint: dashboardApiEndpointConsts.UPDATE_ONBOARDING_AGENCY_CLAIMS,
                payload: {
                    "NewAgencyNumber": args.newAgencyNumber,
                    "OnboardingAgencyNumber":  globalValueConsts.SF_ONBOARDING_AGENCY,
                    "UserFirstLastName": args.userFirstLastName,
                },
            }).then(function (response) {
                return response.data; //Returns true if successful
            });
        },  
        validateSv2AgencyOnboardingSwapAsync(args) {
            return httpClient.callHttpClient({
                method: 'POST',
                endpoint: dashboardApiEndpointConsts.VALIDATE_AGENCY_SV2_ONBOARDING_SWAP,
                payload: {
                    "UserId": args.userId,
                    "AgencyNumber":  args.agencyNumber,
                    "OnboardingAgency": globalValueConsts.SF_ONBOARDING_AGENCY,
                },
            }).then(function (response) {
                return response.data; //Returns true if successful
            });
        }
    },  //end of actions
})
